import { API, graphqlOperation } from "aws-amplify";

import {
  barcodeByInventoryItemIdOnly,
  getAccountNameOnly,
  getInventoryIdOnly,
  getLocationOrphanAudit,
  listInventoryIdsOrphanAudit,
  listInventoryItemsOrphanAudit,
  sealByReferenceIdIdOnly,
  shipmentLineItemByInventoryItemIdOnly,
} from "../../graphql/admin-queries";
import { deleteInventoryItemIdOnlyIf } from "../../graphql/mutations-ids-only";
import { isConditionalCheckFailed, logError } from "../../utils/appUtils";
import { LocationType } from "../../utils/constants";
import { isBulkLocation } from "../../utils/locationUtils";
import {
  graphqlOpWithRetry,
  runPromisesInConcurrencyChunks,
} from "../resetListVariables";

/**
 * Orphan InventoryItems: rows whose parent Inventory no longer exists.
 *
 * The inbound deep delete (DeepDeleteButton -> processInbound /inboundDelete)
 * sent its items as bare { id } from 2023 until 2026-10, and the lambda only
 * deleted items whose inventoryId matched an inventory in the same request.
 * None did, so every deep-deleted inbound left its InventoryItem rows behind.
 * They still show in location views, and because InventoryItem.inventory is
 * non-nullable, any query that resolves the parent fails outright on them.
 *
 * The audit walks every yard. The cleanup only deletes rows the last audit
 * found, after checking each one again:
 *  - the parent Inventory is still missing;
 *  - nothing references the row (an outbound pick, a seal, a barcode);
 *  - the row still names the parent and yard the audit saw (conditional
 *    delete), so a row that changed since the audit is left alone.
 *
 * Location capacity is not touched. The deep delete already sent the location
 * capacity reset for these items when it deleted the inbound; only the row
 * delete was skipped. Yards with orphans in capacity-tracked locations are
 * listed so their capacity can be re-audited with auditCapacityUsed, which
 * cannot run cleanly while the orphans are there (it resolves item.inventory).
 */

const PAGE_LIMIT = 1000;
const CONCURRENCY = 20;

export const OrphanAction = {
  DELETE: "delete",
  REVIEW: "review",
};

// Mirrors resetCapacityUsed: bulk locations and STRUCTURED locations not set
// to the "other" load type keep capacity counters; everything else does not.
export const isCapacityTrackedLocation = (location) => {
  if (!location) return false;
  if (isBulkLocation(location)) return true;
  return (
    location.type === LocationType.STRUCTURED &&
    !location.locationLoad?.other?.selected
  );
};

export const hasReferences = (references) =>
  references.lineItems > 0 || references.seals > 0 || references.barcodes > 0;

const listAll = async (query, rootKey, onPage) => {
  const items = [];
  let nextToken = null;
  do {
    const variables = { limit: PAGE_LIMIT, nextToken };
    const response = await graphqlOpWithRetry(() =>
      API.graphql(graphqlOperation(query, variables)),
    );
    const page = response?.data?.[rootKey];
    items.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken || null;
    if (onPage) onPage(items.length);
  } while (nextToken);
  return items;
};

// Runs one task per key and returns { key: value } for the ones that
// resolved. A key whose lookup failed is reported in `failed` instead of
// being treated as "not found" -- for a parent check that difference is the
// whole point.
const lookupAll = async (keys, lookup) => {
  const tasks = keys.map((key) => () => lookup(key));
  const settled = await runPromisesInConcurrencyChunks(tasks, CONCURRENCY);
  const found = {};
  const failed = [];
  settled.forEach((result, index) => {
    if (result.status === "fulfilled") found[keys[index]] = result.value;
    else failed.push({ key: keys[index], reason: result.reason?.message });
  });
  return { found, failed };
};

// Resolves to true when the Inventory row exists.
const inventoryExists = async (id) => {
  const response = await API.graphql(
    graphqlOperation(getInventoryIdOnly, { id }),
  );
  return Boolean(response?.data?.getInventorie?.id);
};

const fetchLocation = async (id) => {
  const response = await API.graphql(
    graphqlOperation(getLocationOrphanAudit, { id }),
  );
  return response?.data?.getLocation || null;
};

const fetchAccountName = async (id) => {
  const response = await API.graphql(
    graphqlOperation(getAccountNameOnly, { id }),
  );
  const account = response?.data?.getAccount;
  return account ? account.displayName || account.businessName || id : null;
};

const countReferences = async (inventoryItemId) => {
  const [lineItems, seals, barcodes] = await Promise.all([
    API.graphql(
      graphqlOperation(shipmentLineItemByInventoryItemIdOnly, {
        inventoryItemId,
        limit: 1,
      }),
    ),
    API.graphql(
      graphqlOperation(sealByReferenceIdIdOnly, {
        referenceId: inventoryItemId,
        limit: 1,
      }),
    ),
    API.graphql(
      graphqlOperation(barcodeByInventoryItemIdOnly, {
        inventoryItemId,
        limit: 1,
      }),
    ),
  ]);
  return {
    lineItems:
      lineItems?.data?.shipmentLineItemByInventoryItem?.items?.length || 0,
    seals: seals?.data?.sealByReferenceId?.items?.length || 0,
    barcodes:
      barcodes?.data?.barcodeByInventoryItemByActiveByValue?.items?.length || 0,
  };
};

// Parents confirmed missing by a direct read. Candidates come from two table
// scans that are neither atomic nor strongly consistent, so an Inventory
// created mid-scan can look missing; the read settles it. A failed read keeps
// the parent out of the missing set -- not deleting is the safe default.
const confirmMissingParents = async (inventoryIds) => {
  const { found, failed } = await lookupAll(inventoryIds, inventoryExists);
  const missing = new Set(inventoryIds.filter((id) => found[id] === false));
  return { missing, failed };
};

/**
 * One row per yard / account / inbound, for the dashboard table and console.
 */
export const summarizeOrphans = (orphans) => {
  const groups = new Map();
  for (const orphan of orphans) {
    const key = `${orphan.reloadYardId}|${orphan.accountId}|${orphan.inboundShipmentId}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        id: key,
        yard: orphan.yardName,
        account: orphan.accountName,
        inboundShipmentId: orphan.inboundShipmentId,
        items: 0,
        toDelete: 0,
        toReview: 0,
        capacityTracked: 0,
        statuses: {},
        onHand: 0,
        firstCreated: orphan.createdAt,
        lastUpdated: orphan.updatedAt,
      };
      groups.set(key, group);
    }
    group.items += 1;
    if (orphan.action === OrphanAction.DELETE) group.toDelete += 1;
    else group.toReview += 1;
    if (orphan.capacityTracked) group.capacityTracked += 1;
    group.statuses[orphan.status] = (group.statuses[orphan.status] || 0) + 1;
    group.onHand += Number(orphan.quantityOnHand) || 0;
    if (orphan.createdAt && orphan.createdAt < group.firstCreated)
      group.firstCreated = orphan.createdAt;
    if (orphan.updatedAt && orphan.updatedAt > group.lastUpdated)
      group.lastUpdated = orphan.updatedAt;
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      statuses: Object.entries(group.statuses)
        .map(([status, count]) => `${status} ${count}`)
        .join(", "),
    }))
    .sort(
      (a, b) =>
        String(a.yard).localeCompare(String(b.yard)) ||
        String(a.account).localeCompare(String(b.account)) ||
        String(a.firstCreated).localeCompare(String(b.firstCreated)),
    );
};

/**
 * Read-only. Finds every orphan InventoryItem across all yards and classifies
 * it: DELETE when nothing references it, REVIEW when an outbound pick, seal or
 * barcode still points at it.
 *
 * @param {Array} reloadYards - [{ id, name }] for display names
 * @param {function} notify - react-admin notify
 * @returns the audit, which cleanupOrphanInventoryItems takes as input
 */
export const auditOrphanInventoryItems = async (reloadYards = [], notify) => {
  console.clear();
  const startedAt = new Date().toISOString();
  try {
    if (notify) notify("Orphan audit: reading inventory items...");
    const items = await listAll(
      listInventoryItemsOrphanAudit,
      "listInventoryItems",
      (count) => console.log(`[orphanAudit] inventory items read: ${count}`),
    );
    if (notify) notify("Orphan audit: reading inventories...");
    const inventories = await listAll(
      listInventoryIdsOrphanAudit,
      "listInventories",
      (count) => console.log(`[orphanAudit] inventories read: ${count}`),
    );

    const inventoryIds = new Set(inventories.map((inventory) => inventory.id));
    const candidates = items.filter(
      (item) => !item.inventoryId || !inventoryIds.has(item.inventoryId),
    );
    const candidateParents = [
      ...new Set(candidates.map((item) => item.inventoryId).filter(Boolean)),
    ];
    console.log("[orphanAudit] scan", {
      inventoryItems: items.length,
      inventories: inventories.length,
      candidates: candidates.length,
      candidateParents: candidateParents.length,
    });

    const { missing, failed: parentFailures } =
      await confirmMissingParents(candidateParents);
    const orphanRows = candidates.filter(
      (item) => !item.inventoryId || missing.has(item.inventoryId),
    );

    const locationIds = [
      ...new Set(orphanRows.map((item) => item.locationId).filter(Boolean)),
    ];
    const accountIds = [
      ...new Set(orphanRows.map((item) => item.accountId).filter(Boolean)),
    ];
    // One after the other: each already runs CONCURRENCY requests at a time.
    const locations = await lookupAll(locationIds, fetchLocation);
    const accounts = await lookupAll(accountIds, fetchAccountName);
    const references = await lookupAll(
      orphanRows.map((item) => item.id),
      countReferences,
    );
    const yardNames = Object.fromEntries(
      reloadYards.map((yard) => [yard.id, yard.name]),
    );

    const orphans = [];
    const unchecked = [];
    for (const item of orphanRows) {
      const refs = references.found[item.id];
      if (!refs) {
        // Reference lookup failed: it cannot be cleared for deletion, and it
        // is not a confirmed reference either. Reported, never deleted.
        unchecked.push(item.id);
      }
      const location = locations.found[item.locationId] || null;
      orphans.push({
        ...item,
        yardName: yardNames[item.reloadYardId] || item.reloadYardId,
        accountName: item.accountId
          ? accounts.found[item.accountId] || `(missing) ${item.accountId}`
          : "(none)",
        locationName: location?.name || item.locationFullName,
        locationType: location
          ? location.locationLoad?.other?.selected
            ? `${location.type}/other`
            : location.type
          : "(missing)",
        capacityTracked: isCapacityTrackedLocation(location),
        references: refs || null,
        action:
          refs && !hasReferences(refs)
            ? OrphanAction.DELETE
            : OrphanAction.REVIEW,
      });
    }

    const audit = {
      startedAt,
      finishedAt: new Date().toISOString(),
      scannedItems: items.length,
      scannedInventories: inventories.length,
      orphans,
      summary: summarizeOrphans(orphans),
      parentLookupFailures: parentFailures.length,
      referenceLookupFailures: unchecked.length,
    };
    const toDelete = orphans.filter((o) => o.action === OrphanAction.DELETE);
    const capacityYards = [
      ...new Set(
        toDelete.filter((o) => o.capacityTracked).map((o) => o.yardName),
      ),
    ];

    console.log("[orphanAudit] result", {
      orphans: orphans.length,
      toDelete: toDelete.length,
      toReview: orphans.length - toDelete.length,
      yards: new Set(orphans.map((o) => o.reloadYardId)).size,
      accounts: new Set(orphans.map((o) => o.accountId)).size,
      inbounds: new Set(orphans.map((o) => o.inboundShipmentId)).size,
      capacityYards,
      parentLookupFailures: parentFailures.length,
      referenceLookupFailures: unchecked.length,
    });
    console.table(audit.summary);
    const review = orphans.filter((o) => o.action === OrphanAction.REVIEW);
    if (review.length) {
      console.log("[orphanAudit] needs review (not deleted by cleanup)");
      console.table(
        review.map((o) => ({
          id: o.id,
          yard: o.yardName,
          account: o.accountName,
          status: o.status,
          onHand: o.quantityOnHand,
          location: o.locationName,
          lineItems: o.references?.lineItems ?? "lookup failed",
          seals: o.references?.seals ?? "lookup failed",
          barcodes: o.references?.barcodes ?? "lookup failed",
        })),
      );
    }
    if (parentFailures.length)
      console.warn(
        "[orphanAudit] parent reads failed; these items were left out",
        parentFailures,
      );

    if (notify)
      notify(
        `Orphan audit: ${orphans.length} orphan items in ` +
          `${audit.summary.length} inbounds; ${toDelete.length} can be ` +
          `cleaned up, ${orphans.length - toDelete.length} need review. ` +
          `See console.`,
      );
    return audit;
  } catch (err) {
    logError("AdminDashboard:auditOrphanInventoryItems", err, notify);
    return null;
  }
};

/**
 * Deletes the DELETE-class orphans of a prior audit, optionally limited to
 * one yard, re-checking each one first (see the module comment).
 *
 * @param {object} audit - auditOrphanInventoryItems result
 * @param {string} reloadYardId - limit to this yard; empty means every yard
 * @param {function} notify - react-admin notify
 */
export const cleanupOrphanInventoryItems = async (
  audit,
  reloadYardId,
  notify,
) => {
  if (!audit?.orphans) {
    if (notify) notify("Run the orphan audit first.", "warning");
    return null;
  }
  const selected = audit.orphans.filter(
    (orphan) =>
      orphan.action === OrphanAction.DELETE &&
      (!reloadYardId || orphan.reloadYardId === reloadYardId),
  );
  const outcome = {
    selected: selected.length,
    deleted: 0,
    parentBack: 0,
    // Referenced now, or the reference check failed: kept either way.
    kept: 0,
    changed: 0,
    failed: 0,
  };
  const failures = [];

  try {
    const parentIds = [
      ...new Set(selected.map((orphan) => orphan.inventoryId).filter(Boolean)),
    ];
    const { missing } = await confirmMissingParents(parentIds);
    const stillOrphaned = selected.filter((orphan) => {
      if (!orphan.inventoryId || missing.has(orphan.inventoryId)) return true;
      outcome.parentBack += 1;
      return false;
    });

    const references = await lookupAll(
      stillOrphaned.map((orphan) => orphan.id),
      countReferences,
    );
    const toDelete = stillOrphaned.filter((orphan) => {
      const refs = references.found[orphan.id];
      if (refs && !hasReferences(refs)) return true;
      outcome.kept += 1;
      return false;
    });

    const tasks = toDelete.map((orphan) => () => {
      // A row the audit saw with no inventoryId must still have none: one
      // given an inventory since is no longer an orphan.
      const condition = orphan.inventoryId
        ? {
            inventoryId: { eq: orphan.inventoryId },
            reloadYardId: { eq: orphan.reloadYardId },
          }
        : {
            inventoryId: { attributeExists: false },
            reloadYardId: { eq: orphan.reloadYardId },
          };
      return API.graphql(
        graphqlOperation(deleteInventoryItemIdOnlyIf, {
          input: { id: orphan.id },
          condition,
        }),
      );
    });
    const settled = await runPromisesInConcurrencyChunks(tasks, CONCURRENCY);
    settled.forEach((result, index) => {
      if (result.status === "fulfilled") {
        outcome.deleted += 1;
        return;
      }
      const reason = result.reason;
      // Changed since the audit, or already gone: leave it.
      if (
        isConditionalCheckFailed(reason) ||
        isConditionalCheckFailed(reason?.cause)
      ) {
        outcome.changed += 1;
        return;
      }
      outcome.failed += 1;
      failures.push({ id: toDelete[index].id, reason: reason?.message });
    });

    const capacityYards = [
      ...new Set(
        toDelete
          .filter((orphan) => orphan.capacityTracked)
          .map((orphan) => `${orphan.yardName} (${orphan.reloadYardId})`),
      ),
    ];
    console.log("[orphanCleanup] result", { reloadYardId, ...outcome });
    if (failures.length) {
      console.table(failures);
      logError(
        "AdminDashboard:cleanupOrphanInventoryItems",
        { message: `${failures.length} deletes failed`, failures },
        false,
      );
    }
    if (capacityYards.length)
      console.log(
        "[orphanCleanup] orphans were in capacity-tracked locations; run " +
          "auditCapacityUsed for these yards:",
        capacityYards,
      );

    if (notify)
      notify(
        `Orphan cleanup: deleted ${outcome.deleted} of ${outcome.selected}` +
          (outcome.parentBack ? `, ${outcome.parentBack} parent back` : "") +
          (outcome.kept ? `, ${outcome.kept} kept (referenced)` : "") +
          (outcome.changed ? `, ${outcome.changed} changed` : "") +
          (outcome.failed ? `, ${outcome.failed} failed` : "") +
          ". Re-run the audit to confirm. See console.",
        outcome.failed ? "warning" : "info",
      );
    return { ...outcome, failures, capacityYards };
  } catch (err) {
    logError("AdminDashboard:cleanupOrphanInventoryItems", err, notify);
    return null;
  }
};
